import { cloneDeep } from 'lodash';
import { useState } from 'react';
import { InputNumber } from '@meitu/saas-ui';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import type { GetCustomContextMenuFn, IMenuItemProps } from '@/plugins/context-menu';
import type { Cell, ITableLayer } from '@/version-types';
import { cancelMergeCells, mergeCells } from '../../../actions/merge-cells';
import { getRowOrColActions } from '../../../actions/row-col-actions';
import { SIDE_BAR_BOLD } from '../../../default-configs';
import addOrUpdateLayer from '../../../utils/add-or-update-layer';
import { getTableCellInfoListByPoint, getTableLocalPointByScenePoint } from '../../../utils/geometry';
import { getTableEditState } from '../state';
import { TableAction } from '../table-action';
import { type TableLayerEdit } from '../table-edit';
import { getTableFramePoint } from '../utils/get-table-frame-point';

type T = 'add-row-on-top' | 'add-row-on-bottom' | 'add-col-on-left' | 'add-col-on-right';

export const registerTableContextMenu = (frameworkIdentity: string, layerId: string) => {
    const { $t } = getLocaleStore();
    const framework = getFramework(frameworkIdentity);
    const { addOrDelRowOrColByCellsSelectedInfo: _addOrDelRowOrColByCellsSelectedInfo } =
        getRowOrColActions(frameworkIdentity);
    const addOrDelRowOrColByCellsSelectedInfo: typeof _addOrDelRowOrColByCellsSelectedInfo = (data) => {
        new TableAction(frameworkIdentity).emit('addOrDelRowOrColByCellsSelectedInfo');
        return _addOrDelRowOrColByCellsSelectedInfo(data);
    };

    const inputValueMapRef: Record<T, number> = {
        'add-row-on-top': 1,
        'add-row-on-bottom': 1,
        'add-col-on-left': 1,
        'add-col-on-right': 1,
    };

    const Label: React.FC<{
        type: T;
        beforeText: string;
        inputSuffix: string;
        onSubmit: () => void;
    }> = ({ type, beforeText, inputSuffix, onSubmit }) => {
        const [value, setValue] = useState(1);
        return (
            <div
                id={type}
                style={{
                    minWidth: 178,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {beforeText}
                <div onClick={(e) => e.stopPropagation()}>
                    <InputNumber
                        style={{ width: 90, height: 34 }}
                        value={value}
                        min={1}
                        suffix={inputSuffix}
                        showStepBtn={false}
                        enableMouseWheel={false}
                        onInputCapture={(e) => {
                            const { value } = e.currentTarget;
                            if (!/\d*/.test(value)) return;
                            const num = Number(value);
                            inputValueMapRef[type] = num;
                            setValue(num);
                        }}
                        onPressEnter={onSubmit}
                    />
                </div>
            </div>
        );
    };

    Label.displayName = 'CellContextMenuLabel';

    const getMenuItem = ({
        type,
        beforeText,
        inputSuffix,
        extraParams,
    }: {
        type: T;
        beforeText: string;
        inputSuffix: string;
        extraParams?: Partial<IMenuItemProps>;
    }) => {
        const [actionType, targetMode, _, direction] = type.split('-');
        const onClick = () =>
            /* eslint-disable-next-line */
            // @ts-ignore
            addOrDelRowOrColByCellsSelectedInfo({
                actionType,
                targetMode,
                direction,
                addCount: inputValueMapRef[type],
            });

        return {
            type: `custom-${type}`,
            name: <Label type={type} beforeText={beforeText} inputSuffix={inputSuffix} onSubmit={onClick} />,
            disabled: false,
            onClick,
            ...extraParams,
        } as const;
    };

    const customContextMenu: GetCustomContextMenuFn = ({ clientX, clientY }) => {
        const tableLayer = (framework.service.flatLayer.getLayer(layerId) ?? null) as ITableLayer | null;

        if (!tableLayer) return null;

        const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getTableEditState(frameworkIdentity);
        const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
        if (!hasSelectedInfo) return null;

        const scenePoint = getTableFramePoint(framework.service.transform, { x: clientX, y: clientY }, tableLayer);
        const localPoint = getTableLocalPointByScenePoint({ point: scenePoint, tableLayer });
        const sideBarSize = SIDE_BAR_BOLD / framework.service.edgelessCanvas.zoom;
        const hasHitOperateArea =
            (localPoint.x >= 0 &&
                localPoint.x <= tableLayer.width &&
                localPoint.y >= -sideBarSize &&
                localPoint.y <= 0) ||
            (localPoint.x >= -sideBarSize &&
                localPoint.x <= 0 &&
                localPoint.y >= 0 &&
                localPoint.y <= tableLayer.height);
        const hasHitTableLayer =
            hasHitOperateArea ||
            getTableCellInfoListByPoint({
                point: scenePoint,
                tableLayer,
            }).length > 0;

        if (!hasHitTableLayer) return null;

        if (rowOrColSelectedRectInfo) {
            return rowOrColSelectedRectInfo.direction === 'horizontal'
                ? [
                      getMenuItem({
                          type: 'add-row-on-top',
                          beforeText: $t((v) => v.tableLayer.hooks.upAdd),
                          inputSuffix: $t((v) => v.tableLayer.row),
                      }),
                      getMenuItem({
                          type: 'add-row-on-bottom',
                          beforeText: $t((v) => v.tableLayer.hooks.downAdd),
                          inputSuffix: $t((v) => v.tableLayer.row),
                      }),
                      {
                          type: 'custom-delRow',
                          name: $t((v) => v.tableLayer.hooks.delSelectRow),
                          disabled: false,
                          onClick: () =>
                              addOrDelRowOrColByCellsSelectedInfo({
                                  actionType: 'del',
                                  targetMode: 'row',
                              }),
                      },
                  ]
                : [
                      getMenuItem({
                          type: 'add-col-on-left',
                          beforeText: $t((v) => v.tableLayer.hooks.leftAdd),
                          inputSuffix: $t((v) => v.tableLayer.column),
                      }),
                      getMenuItem({
                          type: 'add-col-on-right',
                          beforeText: $t((v) => v.tableLayer.hooks.rightAdd),
                          inputSuffix: $t((v) => v.tableLayer.column),
                      }),
                      {
                          type: 'custom-delCol',
                          name: $t((v) => v.tableLayer.hooks.delSelectColumn),
                          disabled: false,
                          onClick: () =>
                              addOrDelRowOrColByCellsSelectedInfo({
                                  actionType: 'del',
                                  targetMode: 'col',
                              }),
                      },
                  ];
        }

        const targetCellInfoList = cellsSelectedInfo!.cellInfoList;
        const targetCellIds = targetCellInfoList.map((c) => c.id);
        const targetCells = targetCellInfoList.map((c) => tableLayer.data.cells[c.vIndex][c.hIndex]) as Cell[];

        const cellMenuList: IMenuItemProps[] = [
            // 行
            getMenuItem({
                type: 'add-row-on-top',
                beforeText: $t((v) => v.tableLayer.hooks.upAdd),
                inputSuffix: $t((v) => v.tableLayer.row),
                extraParams: {
                    group: 2,
                },
            }),
            getMenuItem({
                type: 'add-row-on-bottom',
                beforeText: $t((v) => v.tableLayer.hooks.downAdd),
                inputSuffix: $t((v) => v.tableLayer.row),
                extraParams: {
                    group: 2,
                },
            }),
            {
                type: 'custom-delRow',
                name: $t((v) => v.tableLayer.hooks.delSelectRow),
                disabled: false,
                group: 2,
                onClick: () =>
                    addOrDelRowOrColByCellsSelectedInfo({
                        actionType: 'del',
                        targetMode: 'row',
                    }),
            },

            // 列
            getMenuItem({
                type: 'add-col-on-left',
                beforeText: $t((v) => v.tableLayer.hooks.leftAdd),
                inputSuffix: $t((v) => v.tableLayer.column),
                extraParams: {
                    group: 3,
                },
            }),
            getMenuItem({
                type: 'add-col-on-right',
                beforeText: $t((v) => v.tableLayer.hooks.rightAdd),
                inputSuffix: $t((v) => v.tableLayer.column),
                extraParams: {
                    group: 3,
                },
            }),
            {
                type: 'custom-delCol',
                name: $t((v) => v.tableLayer.hooks.delSelectColumn),
                disabled: false,
                group: 3,
                onClick: () =>
                    addOrDelRowOrColByCellsSelectedInfo({
                        actionType: 'del',
                        targetMode: 'col',
                    }),
            },

            // 清除内容
            {
                type: 'custom-clear',
                name: $t((v) => v.tableLayer.hooks.clear),
                group: 4,
                disabled: false,
                onClick: () => {
                    let newTableLayer = cloneDeep(tableLayer);
                    targetCellInfoList.forEach((c) => {
                        const cell = newTableLayer.data.cells[c.vIndex][c.hIndex];
                        if (!cell) return;
                        cell.content = '';
                    });
                    newTableLayer = addOrUpdateLayer({
                        frameworkIdentity,
                        layer: newTableLayer,
                        resetMaterialIds: true,
                    });
                    framework.service.frame.setActiveLayer(newTableLayer.id);
                    framework.service.history.write(cloneDeep(tableLayer), newTableLayer);

                    new TableAction(framework.identity).emit('delete');
                },
            },
        ];
        const mergeOperateIsShow = targetCells.length > 1;
        const checkCellIsMerged = (cell: Record<'colSpan' | 'rowSpan', number>): boolean =>
            cell.colSpan > 1 || cell.rowSpan > 1;
        const cancelMergeOperateIsShow = targetCells.length === 1 && checkCellIsMerged(targetCells[0]);
        if (mergeOperateIsShow) {
            cellMenuList.unshift({
                type: 'custom-merge',
                name: $t((v) => v.tableLayer.mergeCell),
                disabled: false,
                group: 1,
                onClick: () =>
                    mergeCells({
                        frameworkIdentity,
                        tableLayer,
                        targetCellIds,
                    }),
            });
        }
        if (cancelMergeOperateIsShow) {
            cellMenuList.unshift({
                group: 1,
                type: 'custom-cancelMerge',
                name: $t((v) => v.tableLayer.cancelMergeCell),
                disabled: false,
                onClick: () =>
                    cancelMergeCells({
                        frameworkIdentity,
                        tableLayer,
                        targetCellId: targetCellIds[0],
                    }),
            });
        }
        return cellMenuList;
    };

    framework.service.registration.getInstance<TableLayerEdit>('TableLayerEdit').registerContextMenu(customContextMenu);
    return framework.service.registration.getInstance<TableLayerEdit>('TableLayerEdit').unregisterContextMenu;
};
